import {
  Button,
  CloseButton,
  Flex,
  Heading,
  Modal,
  Spinner,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { LearningOutcomesService } from "../../services/LearningOutcomesService";
import { UserDetailContext } from "../../utils/Context";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { Selector } from "../Selectors/Selector";
import { getEarliestCanvasTerm } from "../TermManagement/utils";
import { EffectiveTermSelect } from "./EffectiveTermSelect";

const ApproveDialog = ({
  outcome,
  open,
  setOpen,
  versionOptions,
  progId,
  refresh,
}) => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState("new");
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [effectiveTerm, setEffectiveTerm] = useState(undefined);

  useEffect(() => {
    if (selectedClass === "new") {
      setSelectedVersion(null);
      setEffectiveTerm(undefined);
    }
  }, [selectedClass]);

  const handleClose = () => {
    setSelectedClass("new");
    setSelectedVersion(null);
    setEffectiveTerm(undefined);
    setLoading(false);
  };

  const handleApproveOutcome = () => {
    setLoading(true);
    const contextId = progId ?? "ISLO";

    let earliestCanvasTerm = undefined;
    if (effectiveTerm) {
      earliestCanvasTerm = getEarliestCanvasTerm(effectiveTerm);
    }

    LearningOutcomesService.classifyOutcome({
      outcomeId: outcome.id,
      contextId: contextId,
      loClass: selectedClass,
      relatedOutcomeId: selectedVersion?.id ?? null,
      userId: userDetails.canvas_id,
      categoryId: selectedVersion?.category_id,
      effectiveTermId: earliestCanvasTerm?.id,
    })
      .then(() => {
        refresh(true);
        setOpen(false);
      })
      .catch(() =>
        showNotification(t("notifications.analyze.approveError"), ERROR)
      )
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      open={open}
      size="medium"
      label={t("analyze.approveOutcome")}
      onExited={handleClose}
    >
      <Modal.Header>
        <Flex justifyItems="space-between">
          <Flex.Item>
            <Heading level="h2" as="h1">
              {t("analyze.approveOutcome")}
            </Heading>
          </Flex.Item>
          <Flex.Item>
            <CloseButton
              size="small"
              screenReaderLabel={t("global.close")}
              onClick={() => setOpen(false)}
            />
          </Flex.Item>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        <Flex wrap="wrap">
          <Flex.Item size="100%">
            <ClassSelect
              selectedClass={selectedClass}
              selectHandler={(classId) => setSelectedClass(classId)}
            />
          </Flex.Item>
          {selectedClass === "version" && (
            <Flex.Item size="100%" margin="small 0">
              <Selector
                label={t("review.selectOutcome") + ":"}
                options={versionOptions}
                selectHandler={(outcomeId) =>
                  setSelectedVersion(
                    versionOptions.find((o) => o.id === outcomeId)
                  )
                }
                selectedItemId={selectedVersion?.id}
              />
            </Flex.Item>
          )}
          {selectedClass === "version" && (
            <EffectiveTermSelect
              selectHandler={(term) => setEffectiveTerm(term)}
              selectedTermId={effectiveTerm?.id}
            />
          )}
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Button margin="0 small 0 0" onClick={() => setOpen(false)}>
          {t("global.cancel")}
        </Button>
        <Button
          type="submit"
          color="primary"
          onClick={handleApproveOutcome}
          interaction={
            (selectedClass === "new" || !!selectedVersion) && !loading
              ? "enabled"
              : "disabled"
          }
        >
          {loading ? (
            <Spinner size="x-small" renderTitle={t("global.pleaseWait")} />
          ) : (
            t("analyze.approve")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ClassSelect = ({ selectedClass, selectHandler }) => {
  const { t } = useTranslation();

  return (
    <Selector
      label={t("analyze.classSelector")}
      options={[
        { id: "new", label: t("review.new") },
        { id: "version", label: t("review.version") },
      ]}
      selectHandler={selectHandler}
      selectedItemId={selectedClass}
    />
  );
};

export default ApproveDialog;
